import { Component } from '@theme/component';

/**
 * A custom element that manages a cart notification popup.
 *
 * @typedef {object} CartNotificationRefs
 * @property {HTMLElement} notification - The notification element.
 * 
 * @extends Component<CartNotificationRefs>
 */
class CartNotificationComponent extends Component {
  requiredRefs = ['notification'];

  connectedCallback() {
    super.connectedCallback();
    
    this.refs.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
    
    this.onBodyClick = this.handleBodyClick.bind(this);
  }

  /**
   * Opens the cart notification with animation
   */
  open() {
    const { notification } = this.refs;
    
    notification.classList.add('animate', 'active');

    notification.addEventListener('transitionend', () => {
      notification.focus();
      // TODO: Add focus trap if needed
    }, { once: true });

    document.body.addEventListener('click', this.onBodyClick);
  }

  /**
   * Closes the cart notification
   */
  close() {
    const { notification } = this.refs;
    
    notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    
    // TODO: Remove focus trap if implemented
  }

  /**
   * Updates notification content with cart response data
   * @param {Object} parsedState - Response from cart add API
   */
  renderContents(parsedState) {
    this.productId = parsedState.id;
    
    // Show instant notification with current product image
    this.showInstantImage();
    
    // Update all sections including product details
    this.getSectionsToRender().forEach((section) => {
      const element = document.getElementById(section.id);
      if (element && parsedState.sections[section.id]) {
        element.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    // Replace the image with the instant one after sections are loaded
    this.replaceWithInstantImage();

    this.open();
  }

  /**
   * Shows instant image placeholder
   */
  showInstantImage() {
    const productElement = document.getElementById('cart-notification-product');
    if (!productElement) return;

    // Get current product image from the page
    const pageImg = document.querySelector('.product__media img, .product-media img, .product-single__media img');
    
    if (pageImg && pageImg.complete) {
      // Store the instant image for later use
      this.instantImageSrc = pageImg.src;
      this.instantImageElement = pageImg.cloneNode(true);
      this.instantImageElement.className = 'cart-notification-product__image';
      this.instantImageElement.width = 70;
      this.instantImageElement.height = 70;
      this.instantImageElement.style.objectFit = 'cover';
      this.instantImageElement.style.borderRadius = '4px';
    }
  }

  /**
   * Replaces the sections image with the instant image
   */
  replaceWithInstantImage() {
    if (!this.instantImageElement) return;
    
    const notificationImg = document.querySelector(`#cart-notification-product-${this.productId} .cart-notification-product__image`);
    if (notificationImg) {
      notificationImg.parentNode.replaceChild(this.instantImageElement, notificationImg);
    }
  }



  /**
   * Returns sections that need to be updated
   */
  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `#cart-notification-product-${this.productId}`,
      },
      {
        id: 'cart-notification-button'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  /**
   * Extracts HTML content from section response
   */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML || '';
  }

  /**
   * Handles clicks outside the notification to close it
   */
  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.refs.notification && !target.closest('cart-notification-component')) {
      this.close();
    }
  }

  /**
   * Sets the active element for focus management
   */
  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-notification-component')) {
  customElements.define('cart-notification-component', CartNotificationComponent);
}
